import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/permissions";
import { createLogger } from "@/lib/logger";

const logger = createLogger('useUserRole');

interface UseUserRoleOptions {
  /** Only run the query when true. Prevents race conditions with session restoration. */
  enabled?: boolean;
}

export function useUserRole(options?: UseUserRoleOptions) {
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ["user-role"],
    queryFn: async (): Promise<AppRole | null> => {
      logger.debug('Starting role fetch...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        logger.warn('getUser() returned null — session not ready?');
        // Don't cache null when session isn't ready — throw to trigger retry
        throw new Error('User not available yet — session may still be restoring');
      }

      logger.debug('User found, querying role...');

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        logger.warn('Role query failed, will retry:', { error: error.message });
        throw new Error(`Role check error - retrying: ${error.message}`);
      }

      const role = (data?.role as AppRole) ?? null;
      logger.debug(`Role resolved: ${role ?? 'none'}`);
      return role;
    },
    enabled,
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 5 * 60 * 1000,
  });
}
